import React, { Fragment } from 'react';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import WarningIcon from '@mui/icons-material/Warning';
import { FormControlLabel, Radio, RadioGroup, } from '@mui/material';

import Dropzone from '../../../../../shared/dropzone/Dropzone';
import Input from '../../../../../shared/Input';

import { procedureFlows, customExtendedProcedureFlows, matorthoProcedureFlows } from '../../../../../../constants/cases';
import userRoles from '../../../../../../constants/userRoles';

const isWeekend = (date) => {
  const day = date.day();
  return day === 0 || day === 6;
};

export const CustomExtendedStep1 = (props) => {
  const {
    activeCase,
    fileStatuses,
    rejectedFiles,
    date,
    setDate,
    xRays,
    handleXRaysUpload,
    handleRemoveXRay,
    onUploadXRayComplete,
    scans,
    handleScansUpload,
    handleRemoveScan,
    onUploadScanComplete,
    requestPlanning,
    setRequestPlanning,
    requestPlanningDate,
    setRequestPlanningDate,
    additionalEmails,
    setAdditionalEmails,
    mris,
    handleMriUpload,
    handleRemoveMri,
    onUploadMriComplete,
    userRole,
  } = props;
  const activeFlow = activeCase.flow || activeCase.procedureFlow;
  const xRayRequired = customExtendedProcedureFlows.includes(activeFlow) && !matorthoProcedureFlows.includes(activeFlow);

  return (
    <Fragment>
      {activeCase.files?.some((file) => file.status === fileStatuses.REJECTED) && (
        <div className="alert warning">
          <div className="d-flex align-center">
            <WarningIcon color="primary" />
            <div>
              {`Some of the scans have been rejected: 
              ${rejectedFiles.map((file) => file.name).join(' ')}`}
            </div>
          </div>
          {activeCase.notes?.step2 && (
            <div className="m-t-md">Note: {activeCase.notes.step2}</div>
          )}
        </div>
      )}

      {userRole !== userRoles.RADIOLOGY.name && (
        <div className="field-container m-t-lg">
          <div className="secondary font-size-sm uppercase">
            Targeted surgery date
          </div>

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              desktopModeMediaQuery="(min-width: 0px)"
              disabled={userRole === userRoles.RADIOLOGY.name}
              value={date}
              onChange={(newDate) => setDate(newDate)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: { variant: 'standard' },
              }}
            />
          </LocalizationProvider>
        </div>
      )}

      <div className="modal-item-block">
        <div className="block-header">
          {activeCase.procedureFlow === procedureFlows.DOUBLE_LEVEL_OSTEOTOMY
            ? 'UPLOAD CT DICOM*'
            : 'UPLOAD CT/CBCT DICOM*'}
        </div>

        <Dropzone
          type="archive"
          files={scans}
          onUpload={handleScansUpload}
          onRemove={handleRemoveScan}
          icon="scan"
          withProgress
          path={`${activeCase.id}/${activeCase.id}`}
          suffix="scan"
          onUploadComplete={onUploadScanComplete}
        />
      </div>

      <div className="modal-item-block">
        <div className="block-header">
          X-RAY
          {xRayRequired && '*'}
        </div>

        <Dropzone
          files={xRays}
          onUpload={handleXRaysUpload}
          onRemove={handleRemoveXRay}
          icon="x-ray"
          withProgress
          path={`${activeCase.id}/${activeCase.id}`}
          suffix="x-ray"
          onUploadComplete={onUploadXRayComplete}
        />
      </div>

      <div className="modal-item-block">
        <div className="block-header">UPLOAD ADDITIONAL FILES</div>

        <Dropzone
          type="archive"
          files={mris}
          onUpload={handleMriUpload}
          onRemove={handleRemoveMri}
          icon="x-ray"
          withProgress
          path={`${activeCase.id}/${activeCase.id}`}
          suffix="mri"
          onUploadComplete={onUploadMriComplete}
        />
      </div>

      {userRole !== userRoles.RADIOLOGY.name && (
        <div>
          <div className="d-flex space-between">
            <div className="secondary uppercase font-bold font-size-sm">
              Request planning meeting
            </div>

            <RadioGroup
              row
              value={requestPlanning}
              onChange={(e) => setRequestPlanning(e.target.value === 'true')}
            >
              <FormControlLabel value control={<Radio />} label="Yes" />
              <FormControlLabel value={false} control={<Radio />} label="No" />
            </RadioGroup>
          </div>

          {requestPlanning && (
            <>
              <div style={{ width: '50%' }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    desktopModeMediaQuery="(min-width: 0px)"
                    value={requestPlanningDate}
                    onChange={(newDate) => setRequestPlanningDate(newDate)}
                    format="DD/MM/YYYY HH:mm"
                    shouldDisableDate={isWeekend}
                    disablePast
                    slotProps={{
                      textField: { variant: 'standard' },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="m-t-lg">
                <div className="secondary uppercase font-size-xs">
                  Additional attendees (email addresses)
                </div>

                <div className='m-t-sm'>
                  <Input
                    value={additionalEmails}
                    onChange={(e) => setAdditionalEmails(e.target.value)}
                    fullWidth
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Fragment>
  );
};
